import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DEFAULT_ANSWER_LANGUAGE,
  answerLanguageOptions,
  baseLanguage,
  sameLanguage,
} from "../lib/answerLanguages";

/**
 * The proposed answer's language state machine — the single place that decides
 * which language is selected and which text is on screen.
 *
 * It implements the two governing rules of
 * docs/proposed-answer-language-spec.md directly:
 *
 *   G1 — the screen follows the selection. `display` resolves to exactly one of
 *        the text in the selected language or a loading state (rule 1.2). Text in
 *        any other language is never returned, including mid-stream (5.2).
 *   G2 — the backend never moves the operator. `selectedLanguage` only changes
 *        through `selectLanguage` / `adoptText`, both of which are operator
 *        actions. Nothing arriving from the backend writes it.
 *
 * There is deliberately no language-to-text cache: an earlier translation only
 * proves that wording existed in that language at some point (rule 3.1). One
 * text exists at a time — `localText`, the result of the operator's most recent
 * action — which is why there is no precedence chain to arbitrate (rule 1.8).
 *
 * Nor is the pick persisted client-side any more. Rule 2.3 wanted it to survive
 * reloads, and a localStorage copy did that — but only per browser, and it could
 * disagree with the answer that actually exists: it outlived the backend's own
 * (TTL'd) copy, so the picker kept showing a language regeneration had stopped
 * producing. `answerLanguage` carries the server's value and already leads the
 * fallback below, so surviving a reload is now a property of the data rather than
 * of this device. `pick` stays in memory for the current session, which is all
 * G2 needs.
 *
 * Apollo lives outside this hook (`translate` is injected), so the behaviour is
 * testable without a GraphQL provider. `useProposedAnswer` composes the two.
 */

/** The one text currently held client-side, with the language it is in. */
interface HeldText {
  messageId: string | null;
  language: string;
  text: string;
  /** A version the operator picked out of the history outranks a running
   *  generation's stream -- they asked to look at that text. A translation does
   *  not: a run that is producing right now supersedes it. */
  source: "version" | "translation";
}

interface PendingTranslation {
  language: string;
  requestId: number;
  /** Rule 3.5: the pick arrived while a run was going, so the request waits for
   *  the run to finish rather than translating a stale answer. */
  awaitingRun: boolean;
}

export interface UseAnswerLanguageSelectionOptions {
  messageId: string | null | undefined;
  /** The answer the backend currently holds for the revision the pointer is on. */
  answerText: string | null;
  /** That answer's language (the revision's languageCode). */
  answerLanguage: string | null | undefined;
  /** The incoming mail's detected language (rules 2.2, 2.6). */
  detectedLanguage: string | null | undefined;
  /** Live generation stream text, or null when nothing is streaming. */
  streamText?: string | null;
  /** Positive evidence that a generation is producing right now. Drives rule 3.5
   *  (defer the translation) and the stale-answer suppression: while a run is
   *  active the persisted answer is out of date, so only the run's own stream or
   *  a loading state may show. */
  synthesisActive?: boolean;
  /** Sends the pick to the backend (rule 3.1) and resolves with the translated
   *  text. Resolving with null means "the backend has nothing yet". */
  translate: (targetLanguageCode: string) => Promise<string | null>;
  /** Called after a translation landed, so the caller can refetch and pick up the
   *  revision the backend created. Purely a background sync — `display` never
   *  waits on it. */
  onTranslated?: () => void;
  /** Reports a failed translation to the operator (rule 3.7). */
  onError?: (message: string) => void;
}

export interface AnswerLanguageDisplay {
  /** The text to render, in the selected language. Null when there is none. */
  text: string | null;
  /** Rule 1.2/4.4: a loading state stands in for the text while an operation runs. */
  isLoading: boolean;
}

export interface UseAnswerLanguageSelectionReturn {
  selectedLanguage: string;
  /** Rule 2.6: the fixed set plus the mail's detected language. */
  languageOptions: string[];
  /** The operator picked a language (rule 3.1: always sent to the backend). */
  selectLanguage: (code: string) => void;
  /** True while a translation is in flight (rule 4.9: covers the whole call). */
  isTranslating: boolean;
  /** Rule 1.2: exactly one of text or loading. */
  display: AnswerLanguageDisplay;
  /** Adopt text the operator just chose by another route — selecting a version
   *  from the history. Sets the selected language to that text's language
   *  (rule 2.4) and shows it immediately, without a round-trip (rule 1.5). */
  adoptText: (text: string, language: string | null | undefined) => void;
  /** Rule 6.2: the answer was replaced (a paragraph was dismissed), so nothing
   *  held client-side is valid any more — drop it and re-request the selected
   *  language. */
  answerReplaced: () => void;
}

export function useAnswerLanguageSelection({
  messageId,
  answerText,
  answerLanguage,
  detectedLanguage,
  streamText = null,
  synthesisActive = false,
  translate,
  onTranslated,
  onError,
}: UseAnswerLanguageSelectionOptions): UseAnswerLanguageSelectionReturn {
  const currentMessageId = messageId ?? null;

  // Rule 2.2, in order of preference: the language of the version on screen; the
  // last language the operator picked for this mail; the mail's detected
  // language; German. Derived rather than stored so it is already correct on the
  // first render and needs no effect to catch up.
  // The stored pick is read once per mail: within a session `pick` below is the
  // live value, so re-reading storage on every render (this component re-renders
  // on every stream chunk) would buy nothing.
  const initialLanguage =
    baseLanguage(answerLanguage) ?? baseLanguage(detectedLanguage) ?? DEFAULT_ANSWER_LANGUAGE;

  // Set only by operator actions (G2). Scoped to a message id so opening a
  // different mail falls back to the 2.2 resolution above (rule 1.6).
  const [pick, setPick] = useState<{ messageId: string | null; language: string } | null>(
    null,
  );
  const selectedLanguage =
    pick && pick.messageId === currentMessageId ? pick.language : initialLanguage;

  // The one text held client-side (a translation result, or a version the
  // operator selected). Not a cache: it is dropped the moment the operator picks
  // another language (rule 1.3) and is never consulted for a different language.
  const [held, setHeld] = useState<HeldText | null>(null);
  const heldForSelection =
    held && held.messageId === currentMessageId && sameLanguage(held.language, selectedLanguage)
      ? held
      : null;

  const [pending, setPending] = useState<PendingTranslation | null>(null);
  const requestSeq = useRef(0);
  /** What was on screen at the moment of the pick, for the failure restore
   *  (rules 3.7 and 3.8). */
  const restoreRef = useRef<{ language: string; text: string | null } | null>(null);

  // The language the running generation was started in (rule 5.1). Latched on
  // the run's leading edge: a pick during the run does not restart it (3.5), so
  // the run's language is whatever was selected when it began.
  const runLanguageRef = useRef<string | null>(null);
  // Read inside the translate callback, which must see whether a run is going at
  // the moment the response lands, not at the moment it was sent.
  const synthesisActiveRef = useRef(synthesisActive);
  synthesisActiveRef.current = synthesisActive;
  const prevSynthesisActive = useRef(false);
  if (synthesisActive && !prevSynthesisActive.current) {
    runLanguageRef.current = selectedLanguage;
  }
  prevSynthesisActive.current = synthesisActive;
  const runLanguage = runLanguageRef.current ?? baseLanguage(answerLanguage) ?? selectedLanguage;

  // Rule 5.1/5.3: the stream is displayed only while the selected language is
  // the run's own language. Picking another language stops the display (later
  // chunks are not shown); switching back resumes it from wherever it got to,
  // because nothing was dropped — only the display was stopped.
  const streamVisible =
    streamText != null && streamText !== "" && sameLanguage(selectedLanguage, runLanguage);

  const backendText =
    answerText && sameLanguage(answerLanguage, selectedLanguage) ? answerText : null;

  // Rule 1.2: exactly one of the text in the selected language, or loading.
  // Order matters only in that an in-flight operation outranks stale text.
  let display: AnswerLanguageDisplay;
  if (pending) {
    // Rule 3.9: one continuous loading state from the pick until the translation
    // appears, whatever is happening behind it.
    display = { text: null, isLoading: true };
  } else if (heldForSelection?.source === "version") {
    // Rule 1.5: the operator asked to look at this version, so it stays on screen
    // even while a generation streams behind it.
    display = { text: heldForSelection.text, isLoading: false };
  } else if (streamVisible) {
    display = { text: streamText, isLoading: false };
  } else if (synthesisActive) {
    // A run is producing but its stream is not on screen: either it has not
    // emitted yet, or its language is not the selected one. Either way the
    // persisted answer is stale, so only a loading state may show.
    display = { text: null, isLoading: true };
  } else if (heldForSelection !== null) {
    // Rule 1.4: the translation stays for as long as that language remains
    // selected — a later backend refresh does not displace it.
    display = { text: heldForSelection.text, isLoading: false };
  } else if (backendText !== null) {
    display = { text: backendText, isLoading: false };
  } else {
    display = { text: null, isLoading: false };
  }

  /** The text the operator is reading right now, for the failure restore. */
  const displayedText = display.text;
  const displayedLanguage = selectedLanguage;

  const fire = useCallback(
    (requestId: number, language: string) => {
      translate(language)
        .then((text) => {
          // Superseded by a newer pick — that request owns the screen now.
          if (requestSeq.current !== requestId) return;
          if (text == null) {
            // The backend has nothing to hand back yet. If a run is still going
            // it will be retried on the run's trailing edge; otherwise this is a
            // failure like any other.
            if (synthesisActiveRef.current) {
              setPending((p) =>
                p && p.requestId === requestId ? { ...p, awaitingRun: true } : p,
              );
              return;
            }
            throw new Error("Keine Übersetzung erhalten");
          }
          setHeld({ messageId: currentMessageId, language, text, source: "translation" });
          setPending(null);
          onTranslated?.();
        })
        .catch((error: unknown) => {
          if (requestSeq.current !== requestId) return;
          setPending(null);
          // Rules 3.7 + 3.8: put back the text that was on screen before the pick
          // and return the selection to that text's language, so the operator is
          // never left on an empty answer nor on a language they are not reading.
          const restore = restoreRef.current;
          if (restore) {
            setPick({ messageId: currentMessageId, language: restore.language });
            setHeld(
              restore.text !== null
                ? {
                    messageId: currentMessageId,
                    language: restore.language,
                    text: restore.text,
                    source: "translation",
                  }
                : null,
            );
          }
          onError?.(error instanceof Error ? error.message : String(error));
        });
    },
    [translate, currentMessageId, onTranslated, onError],
  );

  const selectLanguage = useCallback(
    (code: string) => {
      const next = baseLanguage(code);
      if (!next) return;
      // Rule 3.3: picking the already-selected language does nothing.
      if (sameLanguage(next, selectedLanguage)) return;

      restoreRef.current = { language: displayedLanguage, text: displayedText };
      setPick({ messageId: currentMessageId, language: next });
      // Rule 1.3: the current text disappears at the moment of the pick. Text in
      // the previously selected language is never visible after it.
      setHeld(null);

      // Rule 3.4: no answer and no run to produce one — nothing to translate.
      if (!answerText && !synthesisActive) {
        setPending(null);
        return;
      }
      // Rule 5.3: switching back to the running run's own language needs no
      // request — that run's output already is that language, and its stream
      // resumes on screen.
      if (synthesisActive && sameLanguage(next, runLanguage)) {
        setPending(null);
        return;
      }

      const requestId = ++requestSeq.current;
      // Rule 3.5: a pick during a run does not abort or restart it. The request
      // waits for the run's complete answer instead.
      setPending({ language: next, requestId, awaitingRun: synthesisActive });
      if (!synthesisActive) fire(requestId, next);
    },
    [
      selectedLanguage,
      displayedLanguage,
      displayedText,
      currentMessageId,
      answerText,
      synthesisActive,
      runLanguage,
      fire,
    ],
  );

  // Rule 3.5, second half: the run finished, so translate its complete answer.
  // The operator has seen one continuous loading state throughout (3.9).
  useEffect(() => {
    if (synthesisActive || !pending?.awaitingRun) return;
    const requestId = pending.requestId;
    setPending({ ...pending, awaitingRun: false });
    fire(requestId, pending.language);
  }, [synthesisActive, pending, fire]);

  const adoptText = useCallback(
    (text: string, language: string | null | undefined) => {
      const next = baseLanguage(language) ?? selectedLanguage;
      // Selecting a version is an operator action, so it may move the selection
      // (rule 2.4) — and it supersedes any translation still in flight.
      requestSeq.current += 1;
      setPending(null);
      setPick({ messageId: currentMessageId, language: next });
      setHeld({ messageId: currentMessageId, language: next, text, source: "version" });
    },
    [currentMessageId, selectedLanguage],
  );

  const answerReplaced = useCallback(() => {
    // Rule 6.2: everything held predates the dismissal, so none of it is reused
    // for any language.
    setHeld(null);
    requestSeq.current += 1;
    // The replacement the backend just wrote is in the answer's own language. When
    // that is the selected language there is nothing to request — the refetch
    // delivers it. Otherwise the answer in the selected language is re-requested.
    if (sameLanguage(selectedLanguage, answerLanguage)) {
      setPending(null);
      return;
    }
    restoreRef.current = { language: selectedLanguage, text: displayedText };
    const requestId = requestSeq.current;
    setPending({ language: selectedLanguage, requestId, awaitingRun: synthesisActive });
    if (!synthesisActive) fire(requestId, selectedLanguage);
  }, [selectedLanguage, answerLanguage, displayedText, synthesisActive, fire]);

  // Rule 1.6: a new generation completing resets the answer area to that answer's
  // own language. Only on the run's trailing edge, and never while a pick is
  // pending — that pick is an operator action and outranks the arriving run
  // (G2: it is the operator who decides which text is wanted).
  const prevRunning = useRef(synthesisActive);
  useEffect(() => {
    const was = prevRunning.current;
    prevRunning.current = synthesisActive;
    if (!was || synthesisActive) return;
    if (pending) return;
    setPick(null);
    setHeld(null);
  }, [synthesisActive, pending]);

  // Never carry a held text or an in-flight request across a mail switch
  // (rule 1.6, first clause).
  useEffect(() => {
    requestSeq.current += 1;
    setPending(null);
    setHeld(null);
    setPick(null);
    runLanguageRef.current = null;
    restoreRef.current = null;
  }, [currentMessageId]);

  const languageOptions = useMemo(
    () => answerLanguageOptions(detectedLanguage),
    [detectedLanguage],
  );

  return {
    selectedLanguage,
    languageOptions,
    selectLanguage,
    isTranslating: pending !== null,
    display,
    adoptText,
    answerReplaced,
  };
}
